import {
  collection,
  addDoc,
  setDoc,
  getDocs,
  doc,
  query,
  orderBy,
  serverTimestamp,
  Timestamp,
  where,
  limit,
  deleteDoc,
} from 'firebase/firestore';
import { firestore } from './config';
import {
  BodyParagraphData,
  WritingSaveData,
  EssayPlanItem,
  ReportPlanItem,
} from '@/types/writing';
import { Task1Type, VisualType } from '@/types/task-bank';

// Define the task type
export type WritingTaskType = 'task1' | 'task2';

// Define the structure for a Body Paragraph in Firestore
// Store type as string, empty string means unselected

// Define the structure of a WritingTask document with structured content
export interface WritingTask {
  id: string;
  userId: string;
  taskType: WritingTaskType;
  prompt: string;
  introduction: string;
  bodyParagraphs: BodyParagraphData[];
  conclusion: string;
  thesisStatement?: string;
  bodyParagraphPlans?: string[];
  essayPlan?: EssayPlanItem[];

  // Task 1 specific fields
  imageUrl?: string;
  overview?: string;
  task1Type?: Task1Type;
  visualType?: VisualType;
  task1Ideas?: unknown; // Can be refined later if structure is known
  reportPlan?: ReportPlanItem[]; // Task 1 report plan

  lastSaved: Timestamp;
  createdAt: Timestamp;
}

// Define the structure for data used when creating/updating
export type WritingTaskData = Omit<
  WritingTask,
  'id' | 'lastSaved' | 'createdAt'
> &
  Partial<Pick<WritingTask, 'lastSaved' | 'createdAt'>>;

const writingCollectionPath = (userId: string) =>
  `users/${userId}/writingTasks`;

/**
 * Creates or updates a writing task document in Firestore with structured data.
 */
export const upsertWritingTask = async (
  userId: string,
  writingData: WritingSaveData,
  taskId?: string | null
): Promise<string> => {
  const writingCollectionRef = collection(
    firestore,
    writingCollectionPath(userId)
  );

  try {
    if (taskId) {
      // Update existing document
      const taskDocRef = doc(firestore, writingCollectionPath(userId), taskId);
      const updateData = {
        ...writingData,
        lastSaved: serverTimestamp(),
      };
      await setDoc(taskDocRef, updateData, { merge: true });
      console.log('Writing task updated with ID:', taskId);
      return taskId;
    } else {
      // Create new document
      const addData = {
        ...writingData,
        userId: userId,
        createdAt: serverTimestamp(),
        lastSaved: serverTimestamp(),
      };
      const docRef = await addDoc(writingCollectionRef, addData);
      console.log('Writing task added with ID:', docRef.id);
      return docRef.id;
    }
  } catch (error) {
    console.error('Error upserting writing task:', error);
    throw error;
  }
};

/**
 * Retrieves all writing tasks for a specific user, ordered by last saved date descending.
 *
 * @param userId The UID of the user.
 * @returns An array of WritingTask objects.
 */
export const getUserWritingTasks = async (
  userId: string
): Promise<WritingTask[]> => {
  const writingCollectionRef = collection(
    firestore,
    writingCollectionPath(userId)
  );
  const q = query(writingCollectionRef, orderBy('lastSaved', 'desc'));

  try {
    const querySnapshot = await getDocs(q);
    const tasks: WritingTask[] = [];
    querySnapshot.forEach((doc) => {
      tasks.push({ id: doc.id, ...doc.data() } as WritingTask);
    });
    return tasks;
  } catch (error) {
    console.error('Error getting user writing tasks:', error);
    throw error;
  }
};

/**
 * Retrieves the single most recent writing task for a specific user and task type.
 *
 * @param userId The UID of the user.
 * @param taskType The type of task ('task1' or 'task2').
 * @returns The most recent WritingTask object, or null if none found.
 */
export const getLatestWritingTask = async (
  userId: string,
  taskType: WritingTaskType
): Promise<WritingTask | null> => {
  const writingCollectionRef = collection(
    firestore,
    writingCollectionPath(userId)
  );
  const q = query(
    writingCollectionRef,
    where('taskType', '==', taskType),
    orderBy('lastSaved', 'desc'),
    limit(1)
  );

  try {
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() } as WritingTask;
    } else {
      return null; // No tasks found for this user and type
    }
  } catch (error) {
    console.error('Error getting latest writing task:', error);
    throw error; // Re-throw the error for the caller to handle
  }
};

/**
 * Deletes a writing task document from Firestore.
 *
 * @param userId The UID of the user who owns the task.
 * @param taskId The ID of the task to delete.
 * @returns A Promise that resolves when the deletion is complete.
 */
export const deleteWritingTask = async (
  userId: string,
  taskId: string
): Promise<void> => {
  try {
    const taskDocRef = doc(firestore, writingCollectionPath(userId), taskId);
    await deleteDoc(taskDocRef);
    console.log('Writing task deleted with ID:', taskId);
  } catch (error) {
    console.error('Error deleting writing task:', error);
    throw error;
  }
};
